import { ButtonHTMLAttributes, ReactNode } from "react";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "outline"
  | "danger"
  | "plain"
  | "login"
  | "pillPrimary"
  | "pillOutline"
  | "pillDanger";
type ButtonSize = "md" | "sm" | "cta" | "none";
type ButtonShape = "md" | "lg" | "full" | "none";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  children: ReactNode;
}

const variantClass: Record<ButtonVariant, string> = {
  primary:
    "bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed",
  secondary:
    "bg-gray-200/80 text-gray-800 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed",
  ghost:
    "bg-transparent text-inherit hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed",
  outline:
    "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed",
  danger:
    "bg-white text-red-500 border border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed",
  login:
    "justify-center w-full bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed",
  pillPrimary:
    "justify-center px-5 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed",
  pillOutline:
    "justify-center px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-full hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed",
  pillDanger:
    "justify-center px-4 py-2.5 text-sm font-medium text-red-500 border border-red-200 rounded-full hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed",
  /** No color/shape styles — use `className` to keep call-site appearance exact. */
  plain: "",
};

const sizeClass: Record<ButtonSize, string> = {
  md: "justify-center gap-2 px-5 py-2.5 text-sm font-medium",
  sm: "justify-center gap-2 px-3 py-1.5 text-sm font-medium",
  cta: "justify-center gap-2 px-4 py-2.5 text-sm font-medium",
  none: "",
};

const shapeClass: Record<ButtonShape, string> = {
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
  none: "",
};

const isSelfStyled = (variant: ButtonVariant) =>
  variant === "plain" ||
  variant === "login" ||
  variant === "pillPrimary" ||
  variant === "pillOutline" ||
  variant === "pillDanger";

const Button = ({
  variant = "primary",
  size,
  shape,
  children,
  className = "",
  type = "button",
  ...rest
}: ButtonProps) => {
  const resolvedSize = size ?? (isSelfStyled(variant) ? "none" : "md");
  const resolvedShape =
    shape ?? (resolvedSize === "none" || isSelfStyled(variant) ? "none" : "md");

  return (
    <button
      type={type}
      className={`inline-flex items-center transition-colors ${variantClass[variant]} ${sizeClass[resolvedSize]} ${shapeClass[resolvedShape]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

export default Button;
